import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';
import {object, boolean} from 'yup';

import {useGetTenantV2Query} from '@dms/api/base';
import i18n from '@dms/i18n';
import {useCurrencies} from '@dms/shared';

import {yupString} from 'shared/web';

import type {BillingInformationDetailFormValues} from '../types/BillingInformationDetailFormValues';

type FormValues = BillingInformationDetailFormValues['bankAccounts'][number];

interface DeputyPersonFormProps {
  handleSubmit: (values: FormValues) => void;
  defaultValues?: FormValues;
}

export function BankAccountForm(props: DeputyPersonFormProps) {
  const {data: tenant} = useGetTenantV2Query();
  const {currencyOptions} = useCurrencies();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    const submitValues: FormValues = {
      ...values,
    };

    await props.handleSubmit(submitValues);
    closeCurrentDialog();
  };

  const defaultValues = {
    ...props.defaultValues,
    currency: props.defaultValues?.currency ?? tenant?.currency,
  };

  return (
    <Form<FormValues> defaultValues={defaultValues} onSubmit={handleSubmit} schema={schema}>
      {(control) => (
        <VStack spacing={5}>
          <Grid columns={4}>
            <GridItem span={4}>
              <FormField
                control={control}
                name="accountName"
                type="text"
                isRequired
                label={i18n.t('entity.bank.labels.accountName')}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                control={control}
                name="accountNumber"
                type="text"
                isRequired
                label={i18n.t('entity.bank.labels.accountNumber')}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                control={control}
                name="bankName"
                isRequired
                type="text"
                label={i18n.t('entity.bank.labels.name')}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                control={control}
                name="iban"
                type="text"
                label={i18n.t('entity.bank.labels.iban')}
              />
            </GridItem>
            <FormField
              control={control}
              name="swift"
              type="text"
              label={i18n.t('entity.bank.labels.swift')}
            />

            <FormField
              control={control}
              name="currency"
              isNotClearable
              type="choice"
              label={i18n.t('entity.bank.labels.currency')}
              options={currencyOptions}
            />
            <GridItem span={4}>
              <FormField
                control={control}
                name="isPrimary"
                type="checkbox"
                label={i18n.t('entity.bank.labels.primaryAccount')}
              />
            </GridItem>
          </Grid>

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              onClick={closeCurrentDialog}
              variant="secondary"
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const schema = object({
  accountName: yupString.required(),
  accountNumber: yupString.required(),
  bankName: yupString.required(),
  iban: yupString.default(null),
  swift: yupString.default(null),
  currency: yupString.default(null),
  isPrimary: boolean().default(false),
  isExpanded: boolean().default(false),
});
